import { TemplateElement, html, defineElement, optionalAttribute } from '@webtides/element-js';
import style from './share-button.css';
import '../button/button.js';
import '../icon/icon.js';

/**
 * A CTA button that shares content via the native Web Share API. Falls back to
 * copying the link to the clipboard when sharing (or file sharing) is not
 * supported by the browser.
 *
 * @property {string} label - The button label (e.g. "Teilen").
 * @property {string} copiedLabel - Label shown briefly after the link was copied.
 * @property {string} url - URL to share / copy.
 * @property {boolean} shareFile - When true, also fetch the resource at `url` and share it as a file.
 * @property {string} fileName - File name used when sharing the file.
 * @property {string} shareTitle - Title passed to the share sheet.
 * @property {string} element - Tracking element name, forwarded to the link for the global click tracker.
 */
export default class ShareButton extends TemplateElement {
    _copied = false;

    constructor() {
        super({ styles: [style] });
    }

    properties() {
        return {
            label: 'Teilen',
            copiedLabel: 'Link kopiert',
            url: '',
            shareFile: false,
            fileName: 'file',
            shareTitle: '',
            element: '',
        };
    }

    events() {
        return {
            'mb-button': {
                click: (event) => {
                    event.preventDefault();
                    this.share();
                },
            },
        };
    }

    template() {
        const dataTracking = this.element
            ? JSON.stringify({
                  event: 'generic',
                  category: 'interaction',
                  action: 'body',
                  label: `share - ${this.url}`,
              })
            : '';

        return html`
            <mb-button
                variant="primary"
                href="${this.url}"
                ${optionalAttribute(this.element, 'element', this.element)}
                ${optionalAttribute(dataTracking, 'data-tracking', dataTracking)}
            >
                <mb-icon name="app--share"></mb-icon>
                <span>${this._copied ? this.copiedLabel : this.label}</span>
            </mb-button>
        `;
    }

    async share() {
        const title = this.shareTitle || document.title;
        const shareData = { title };

        try {
            if (this.shareFile && navigator.canShare) {
                const file = await this.fetchFile();
                if (file && navigator.canShare({ files: [file] })) {
                    await navigator.share({ ...shareData, files: [file] });
                    return;
                }
            }

            if (navigator.share) {
                await navigator.share({ ...shareData, url: this.url });
                return;
            }

            await this.copyFallback();
        } catch (error) {
            // The user dismissing the native share sheet throws AbortError — ignore it.
            if (error?.name !== 'AbortError') {
                await this.copyFallback();
            }
        }
    }

    async fetchFile() {
        try {
            const response = await fetch(this.url);
            const blob = await response.blob();
            const type = blob.type || 'application/octet-stream';
            const extension = type.split('/')[1] || 'bin';
            const name = `${this.fileName.replace(/[^\w-]+/g, '-') || 'file'}.${extension}`;
            return new File([blob], name, { type });
        } catch (error) {
            return null;
        }
    }

    async copyFallback() {
        try {
            await navigator.clipboard.writeText(this.url);
            this._copied = true;
            this.requestUpdate();
            window.clearTimeout(this._copiedTimeout);
            this._copiedTimeout = window.setTimeout(() => {
                this._copied = false;
                this.requestUpdate();
            }, 2000);
        } catch (error) {
            // Clipboard access can be denied — nothing more we can do here.
        }
    }
}

defineElement('mb-share-button', ShareButton);
